package com.fantasytenis.shared.data.survivor.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ===== SURVIVOR CONTEST DTOs =====

@Serializable
data class SurvivorPayoutTierDto(
    @SerialName("minRank") val minRank: Int,
    @SerialName("maxRank") val maxRank: Int,
    val amount: Int? = null,
    val percentage: Double? = null,
    val description: String? = null
)

@Serializable
data class SurvivorContestDto(
    val id: String,
    val name: String,
    val description: String? = null,
    @SerialName("drawId") val drawId: String,
    @SerialName("tournamentName") val tournamentName: String = "",
    val variation: Int = 1,
    @SerialName("entryFee") val entryFee: Int = 0,
    @SerialName("prizePool") val prizePool: Int = 0,
    @SerialName("maxEntries") val maxEntries: Int? = null,
    @SerialName("currentEntries") val currentEntries: Int = 0,
    @SerialName("aliveEntries") val aliveEntries: Int = 0,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("currentRound") val currentRound: Int? = null,
    @SerialName("roundDeadline") val roundDeadline: String? = null,
    val status: Int = 1,
    @SerialName("strikeLimit") val strikeLimit: Int = 0,
    @SerialName("rebuyDeadlineRound") val rebuyDeadlineRound: Int? = null,
    @SerialName("rebuyFee") val rebuyFee: Int = 0,
    @SerialName("payoutStructure") val payoutStructure: String? = null, // JSON string
    @SerialName("isFeatured") val isFeatured: Boolean = false,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = "",
    // Lock override fields for admin manual control
    @SerialName("lockOverride") val lockOverride: Int = 1, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    @SerialName("lockOverrideBy") val lockOverrideBy: String = "",
    @SerialName("lockOverrideAt") val lockOverrideAt: String = "",
    @SerialName("lockOverrideReason") val lockOverrideReason: String = "",
    @SerialName("isManuallyUnlocked") val isManuallyUnlocked: Boolean = false
)

// ===== SURVIVOR ENTRY DTOs =====

@Serializable
data class SurvivorEntryDto(
    val id: String,
    @SerialName("contestId") val contestId: String,
    @SerialName("userId") val userId: String,
    @SerialName("userName") val userName: String? = null,
    val status: Int = 1,
    @SerialName("strikesUsed") val strikesUsed: Int = 0,
    @SerialName("survivedRounds") val survivedRounds: Int = 0,
    @SerialName("eliminatedInRound") val eliminatedInRound: Int? = null,
    @SerialName("eliminatedByMatchId") val eliminatedByMatchId: String? = null,
    val rank: Int? = null,
    @SerialName("payoutAmount") val payoutAmount: Int = 0,
    @SerialName("rebuyCount") val rebuyCount: Int = 0,
    @SerialName("lastPickRound") val lastPickRound: Int? = null,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("eliminatedAt") val eliminatedAt: String? = null
)

// ===== SURVIVOR PICK DTOs =====

@Serializable
data class SurvivorPickDto(
    val id: String,
    @SerialName("entryId") val entryId: String,
    val round: Int,
    @SerialName("playerId") val playerId: String,
    @SerialName("playerName") val playerName: String,
    @SerialName("playerSeed") val playerSeed: Int? = null,
    @SerialName("playerCountry") val playerCountry: String? = null,
    @SerialName("opponentId") val opponentId: String? = null,
    @SerialName("opponentName") val opponentName: String? = null,
    @SerialName("opponentSeed") val opponentSeed: Int? = null,
    @SerialName("matchId") val matchId: String? = null,
    @SerialName("matchTime") val matchTime: String? = null,
    val result: Int = 1,
    @SerialName("matchScore") val matchScore: String? = null,
    @SerialName("pickedAt") val pickedAt: String = "",
    @SerialName("resolvedAt") val resolvedAt: String? = null
)

// ===== AVAILABLE PLAYER DTOs =====

@Serializable
data class AvailablePlayerDto(
    @SerialName("playerId") val playerId: String,
    @SerialName("playerName") val playerName: String,
    val seed: Int? = null,
    val country: String? = null,
    @SerialName("atpRanking") val atpRanking: Int? = null,
    @SerialName("matchId") val matchId: String,
    @SerialName("opponentId") val opponentId: String? = null,
    @SerialName("opponentName") val opponentName: String? = null,
    @SerialName("opponentSeed") val opponentSeed: Int? = null,
    @SerialName("matchTime") val matchTime: String? = null,
    val court: String? = null,
    @SerialName("ownershipPercentage") val ownershipPercentage: Double = 0.0,
    @SerialName("alreadyUsed") val alreadyUsed: Boolean = false,
    @SerialName("isMatchLocked") val isMatchLocked: Boolean = false
)

// ===== ROUND SUMMARY DTOs =====

@Serializable
data class SurvivorRoundSummaryDto(
    val round: Int,
    @SerialName("roundStatus") val roundStatus: Int = 1,
    val deadline: String? = null,
    @SerialName("totalEntries") val totalEntries: Int = 0,
    @SerialName("aliveAfter") val aliveAfter: Int = 0,
    @SerialName("eliminatedCount") val eliminatedCount: Int = 0,
    @SerialName("matchesCompleted") val matchesCompleted: Int = 0,
    @SerialName("totalMatches") val totalMatches: Int = 0
)

// ===== REQUEST DTOs =====

@Serializable
data class CreateSurvivorContestRequest(
    val name: String,
    val description: String? = null,
    @SerialName("drawId") val drawId: String,
    val variation: Int = 1,
    @SerialName("entryFee") val entryFee: Int,
    @SerialName("prizePool") val prizePool: Int,
    @SerialName("maxEntries") val maxEntries: Int? = null,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("strikeLimit") val strikeLimit: Int? = null,
    @SerialName("rebuyDeadlineRound") val rebuyDeadlineRound: Int? = null,
    @SerialName("rebuyFee") val rebuyFee: Int? = null,
    @SerialName("payoutStructure") val payoutStructure: List<SurvivorPayoutTierDto>? = null,
    @SerialName("isFeatured") val isFeatured: Boolean = false
)

@Serializable
data class MakeSurvivorPickRequest(
    @SerialName("playerId") val playerId: String
)

@Serializable
data class UpdateSurvivorPickRequest(
    @SerialName("playerId") val playerId: String
)

// ===== RESPONSE DTOs =====

@Serializable
data class SurvivorApiResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: T? = null
)

@Serializable
data class SurvivorListResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: List<T>? = null,
    val total: Int = 0
)

@Serializable
data class SurvivorEntryWithPicksDto(
    val status: Int = 0,
    val error: String? = null,
    val data: SurvivorEntryDto? = null,
    val picks: List<SurvivorPickDto>? = null
)

@Serializable
data class AvailablePlayersResponseDto(
    val status: Int = 0,
    val error: String? = null,
    val data: List<AvailablePlayerDto>? = null
)

@Serializable
data class SurvivorLeaderboardResponseDto(
    val status: Int = 0,
    val error: String? = null,
    val data: List<SurvivorEntryDto>? = null,
    val total: Int = 0,
    @SerialName("aliveCount") val aliveCount: Int = 0
)

@Serializable
data class SurvivorRoundSummaryResponseDto(
    val status: Int = 0,
    val error: String? = null,
    val data: List<SurvivorRoundSummaryDto>? = null
)

// ===== ADMIN LOCK OVERRIDE DTOs =====

@Serializable
data class SetSurvivorLockOverrideRequest(
    @SerialName("lockOverride") val lockOverride: Int, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    val reason: String? = null
)

@Serializable
data class SetSurvivorLockOverrideResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: SurvivorContestDto? = null
)

// ===== ADMIN DELETE ENTRY DTOs =====

@Serializable
data class AdminDeleteSurvivorEntryRequest(
    val reason: String
)

@Serializable
data class AdminDeleteSurvivorEntryResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: SurvivorEntryDto? = null,
    @SerialName("refundProcessed") val refundProcessed: Boolean = false,
    @SerialName("refundTransactionId") val refundTransactionId: String? = null
)
